import { Bell, Plus, Trash2 } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type SocialAlertsConfig = GuildConfig["social_alerts"];
type SocialAlert = SocialAlertsConfig["alerts"][number];

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: SocialAlertsConfig) => void;
};

const emptyAlert = (): SocialAlert => ({
  id: `social_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  enabled: true,
  name: "New social alert",
  platform: "rss",
  feed_url: "",
  channel_id: "",
  message: "New post from {name}",
  embed_color: "#5865F2",
  mention_role_ids: [],
});

export function SocialAlertsTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.social_alerts ?? { enabled: false, check_minutes: 10, alerts: [] };
  const textChannels = channels.filter((ch) => ch.type === 0);
  const roleOptions = roles.map((r) => ({ id: r.id, name: r.name, color: r.color }));

  const update = <K extends keyof SocialAlertsConfig>(key: K, value: SocialAlertsConfig[K]) => {
    onChange({ ...cfg, [key]: value });
  };

  const updateAlert = (idx: number, patch: Partial<SocialAlert>) => {
    const next = [...(cfg.alerts || [])];
    next[idx] = { ...next[idx], ...patch };
    update("alerts", next);
  };

  return (
    <Section
      icon={Bell}
      title="Social Alerts"
      description="Post new social/RSS feed items into Discord as clean embeds."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
      actions={
        <Button size="sm" onClick={() => update("alerts", [...(cfg.alerts || []), emptyAlert()])}>
          <Plus className="h-4 w-4" />
          Add alert
        </Button>
      }
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>No slash commands.</strong> The bot polls each RSS/social feed on the interval below and posts new
          items to the chosen channel with your title/color/mentions. Keep intervals reasonable to avoid rate limits.
        </p>
      </div>

      <Field label="Check every X minutes" hint="Minimum 5 minutes to avoid spam and rate limits.">
        <Input
          type="number"
          min={5}
          max={1440}
          value={cfg.check_minutes}
          onChange={(e) => update("check_minutes", Math.max(5, Number(e.target.value) || 10))}
        />
      </Field>

      <div className="space-y-3">
        {(cfg.alerts || []).map((alert, idx) => (
          <div key={alert.id || idx} className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={alert.enabled} onCheckedChange={(v) => updateAlert(idx, { enabled: v })} />
              <Input
                value={alert.name}
                onChange={(e) => updateAlert(idx, { name: e.target.value })}
                placeholder="YouTube uploads"
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => update("alerts", (cfg.alerts || []).filter((_, i) => i !== idx))}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Platform">
                <Select
                  value={alert.platform}
                  onValueChange={(v) => updateAlert(idx, { platform: v as SocialAlert["platform"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="twitch">Twitch</SelectItem>
                    <SelectItem value="x">X / Twitter</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="reddit">Reddit</SelectItem>
                    <SelectItem value="rss">RSS feed</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Post channel">
                <ChannelPicker
                  channels={textChannels}
                  value={alert.channel_id}
                  onChange={(v) => updateAlert(idx, { channel_id: v === "__none__" ? "" : v })}
                />
              </Field>
              <Field label="Embed color">
                <Input
                  type="color"
                  value={alert.embed_color || "#5865F2"}
                  onChange={(e) => updateAlert(idx, { embed_color: e.target.value })}
                />
              </Field>
            </div>

            <Field label="Feed URL" hint="Use a direct RSS/Atom feed URL. YouTube channel feeds work too.">
              <Input
                value={alert.feed_url}
                onChange={(e) => updateAlert(idx, { feed_url: e.target.value })}
                placeholder="https://www.youtube.com/feeds/videos.xml?channel_id=..."
              />
            </Field>

            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Message template" hint="Variables: {name}, {title}, {url}">
                <Input
                  value={alert.message}
                  onChange={(e) => updateAlert(idx, { message: e.target.value })}
                  placeholder="New post from {name}: {title}"
                />
              </Field>
              <Field label="Mention roles">
                <MultiSelect
                  options={roleOptions}
                  value={alert.mention_role_ids}
                  onChange={(ids) => updateAlert(idx, { mention_role_ids: ids })}
                  prefix="@"
                  placeholder="+ role"
                />
              </Field>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
